import { getProdSupabaseClient } from '@/lib/supabase/client';

/**
 * Check if GA data exists in prod schema base tables vs materialized view
 */

export async function debugGAProdSchema() {
  console.log('🔍 Checking GA data in PROD SCHEMA base tables vs materialized view...');

  try {
    const prodClient = getProdSupabaseClient();

    // Check if GA exists in the prod.cars table
    console.log('📊 Step 1: Checking prod.cars table for GA data...');
    const { count: carsCount, error: carsError } = await prodClient
      .from('cars')
      .select('*', { count: 'exact', head: true })
      .eq('seller_region', 'GA');

    console.log(
      `📊 Prod cars table - GA records: ${carsCount || 0}`,
      carsError ? `Error: ${carsError.message}` : '✅ OK'
    );

    // Check total cars in prod schema
    const { count: totalCarsCount, error: totalCarsError } = await prodClient
      .from('cars')
      .select('*', { count: 'exact', head: true });

    console.log(
      `📊 Prod cars table - Total records: ${totalCarsCount || 0}`,
      totalCarsError ? `Error: ${totalCarsError.message}` : '✅ OK'
    );

    // Check what states exist in prod.cars
    console.log('📊 Step 2: Getting all states from prod.cars...');
    const { data: allStatesInCars, error: statesError } = await prodClient
      .from('cars')
      .select('seller_region')
      .not('seller_region', 'is', null)
      .limit(10000); // Get a large sample

    if (statesError) {
      console.error('❌ Error getting states from prod.cars:', statesError);
    } else if (allStatesInCars) {
      const stateSet = new Set(allStatesInCars.map(r => r.seller_region));
      console.log(
        `📍 States in prod.cars (${allStatesInCars.length} records sampled): ${Array.from(stateSet).sort().join(', ')}`
      );
      console.log(`📍 GA in prod.cars: ${stateSet.has('GA') ? 'YES' : 'NO'}`);
    }

    // Check safety_recalls table structure
    console.log('📊 Step 3: Checking prod.safety_recalls table...');
    const { count: recallsCount, error: recallsError } = await prodClient
      .from('safety_recalls')
      .select('*', { count: 'exact', head: true })
      .limit(1);

    console.log(
      `📊 Prod safety_recalls table - Total records: ${recallsCount || 0}`,
      recallsError ? `Error: ${recallsError.message}` : '✅ OK'
    );

    // Check materialized view state breakdown
    console.log('📊 Step 4: Analyzing materialized view state distribution...');
    const { data: mvStates, error: mvStatesError } = await prodClient
      .from('cars_with_safety_recalls_mv')
      .select('seller_region')
      .not('seller_region', 'is', null)
      .limit(10000); // Sample to avoid large dataset

    if (mvStatesError) {
      console.error('❌ Error getting states from MV:', mvStatesError);
    } else if (mvStates) {
      const mvStateSet = new Set(mvStates.map(r => r.seller_region));
      const stateCounts = mvStates.reduce((acc: Record<string, number>, r) => {
        acc[r.seller_region] = (acc[r.seller_region] || 0) + 1;
        return acc;
      }, {});

      console.log(`📍 States in materialized view: ${Array.from(mvStateSet).sort().join(', ')}`);
      console.log('📍 State counts in MV:', stateCounts);
      console.log(`📍 GA in materialized view: ${mvStateSet.has('GA') ? 'YES' : 'NO'}`);
    }

    // Check if there are cars with safety recalls for GA in the base tables
    console.log('📊 Step 5: Checking if GA cars have safety recalls in base data...');

    // This is a complex query - let's see if we can find GA cars that should have recalls
    const { data: gaCarsWithRecalls, error: gaRecallsError } = await prodClient
      .from('cars')
      .select(
        `
        id,
        vin,
        seller_region,
        make_name,
        model_name,
        car_year
      `
      )
      .eq('seller_region', 'GA')
      .limit(10);

    if (gaRecallsError) {
      console.error('❌ Error getting GA cars:', gaRecallsError);
    } else {
      console.log(
        `📋 GA cars sample (${gaCarsWithRecalls?.length || 0} records):`,
        gaCarsWithRecalls?.slice(0, 3)
      );
    }

    return {
      prodCarsGA: carsCount || 0,
      prodCarsTotal: totalCarsCount || 0,
      prodRecallsTotal: recallsCount || 0,
      mvStatesCount: mvStates?.length || 0
    };
  } catch (error) {
    console.error('❌ Debug failed:', error);
    return null;
  }
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugGAProdSchema = debugGAProdSchema;
}

export default debugGAProdSchema;
